import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { ArrowLeft, BookOpen, Printer, Brain } from 'lucide-react';
import { toast } from 'sonner';
import PracticeScenario from '../shared/PracticeScenario';
import PageMessaging from '../shared/PageMessaging';

export default function InternalMapReality() {
  const navigate = useNavigate();
  const printPage = () => { window.print(); toast.success('Print dialog opened'); };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <style>{`@media print { body { background: white !important; } .no-print { display: none !important; } .print-page { background: white !important; color: black !important; } }`}</style>
      
      <div className="border-b border-slate-700/50 bg-slate-900/90 backdrop-blur-sm sticky top-0 z-50 no-print">
        <div className="container mx-auto px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button onClick={() => navigate('/belonging-ebook')} variant="outline" size="sm" className="border-slate-600 hover:border-slate-500 text-white hover:bg-slate-800">
                <ArrowLeft className="h-4 w-4 mr-2" />Back to Guide
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-white">Our Internal Map of Reality</h1>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Button onClick={printPage} variant="outline" size="sm" className="border-blue-600 hover:bg-blue-900/20 text-white">
                <Printer className="h-4 w-4 mr-2" />Print
              </Button>
              <div className="flex items-center gap-2 text-slate-400 text-sm">
                <BookOpen className="h-4 w-4" />
                <span>Page 51</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 py-12 print-page">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Brain className="h-16 w-16 text-cyan-400 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-white mb-4">Our Internal Map of Reality</h2>
            <p className="text-slate-300 leading-relaxed">
              How we create our experience of the world
            </p>
          </div>

          {/* The Diagram */}
          <div className="bg-white rounded-lg p-6 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_belonging-sessions-1/artifacts/7stvyx7d_Screenshot%202025-11-15%20at%203.24.11%E2%80%AFAM.png"
              alt="Our Internal Map of Reality - Filters, Emotional State, Behavior, Internal Representation"
              className="w-full h-auto rounded"
              style={{ imageRendering: 'high-quality', maxWidth: '100%' }}
            />
          </div>

          {/* How It Works */}
          <div className="bg-gradient-to-br from-purple-900/20 to-pink-900/20 border border-purple-700/30 rounded-lg p-8 mb-8">
            <h3 className="text-2xl font-bold text-white mb-6">How the Internal Map Works</h3>
            <div className="space-y-4 text-slate-200">
              <p className="leading-relaxed">
                <span className="font-semibold text-purple-200">1. Input from Environment:</span> You receive various forms of input from the environment—you see, hear, touch, smell, or taste something.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-purple-200">2. Filters:</span> This sensory input, as it comes in, is filtered. These filters include your beliefs, values, memories, decisions, the language you speak, how you sort, store, and retrieve information, and your strategies for making decisions.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-purple-200">3. Delete, Distort, Generalize:</span> Filters delete, distort, and generalize the input as it comes in, based on the way your Internal Map of Reality has been set up to filter—all of which happens in a split second.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-purple-200">4. Internal Representations:</span> With what's left after the filtering process, you make what are called internal representations. These include internal pictures, hearing internal sounds, feeling internal kinesthetic sensations, smelling internal smells, tasting internal tastes, and having internal dialog—talking to yourself.
              </p>
              <p className="leading-relaxed">
                <span className="font-semibold text-purple-200">5. Emotional States & Behaviors:</span> Internal representations in turn generate emotional states and behaviors. And though you're aware of some of this, almost all of it is going on outside your conscious awareness for your whole life.
              </p>
            </div>
          </div>

          {/* Key Insight */}
          <div className="bg-gradient-to-br from-green-900/20 to-emerald-900/20 border border-green-700/30 rounded-lg p-8">
            <h3 className="text-2xl font-bold text-white mb-4">The Power of Conscious Choice</h3>
            <p className="text-green-100 leading-relaxed mb-4">
              Internal states, behaviors, and internal representations are an interconnected system, where a change in any one changes the other two.
            </p>
            <p className="text-green-200 text-lg font-semibold">
              If you can learn to consciously generate your internal representations, you can be in charge of your internal states, your behavior, and the people and situations that come into your life.
            </p>
          </div>

          <PracticeScenario pageTitle="Our Internal Map of Reality" pageNumber={51} />
          <PageMessaging pageTitle="Our Internal Map of Reality" pageNumber={51} />
        </div>
      </div>
    </div>
  );
}
